const sectionStyle = {
  maxWidth: "900px",
  margin: "0 auto",
  padding: "60px 20px"
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: "20px"
};

const cardStyle = {
  border: "1px solid #eee",
  borderRadius: "12px",
  padding: "20px",
  background: "#fff",
  boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
  transition: "all 0.2s ease"
};

const hoverEffect = (e, enter = true) => {
  e.currentTarget.style.boxShadow = enter
    ? "0 15px 35px rgba(0,0,0,0.1)"
    : "0 10px 25px rgba(0,0,0,0.05)";
  e.currentTarget.style.transform = enter
    ? "translateY(-3px)"
    : "translateY(0)";
};

const AwsCourseUdemy = () => {
  return (
    <div>

      {/* HERO */}
      <div style={{ ...sectionStyle, paddingTop: "80px" }}>
        <h1 style={{ fontSize: "36px", marginBottom: "20px" }}>
          Real-World AWS Development — Practical Cloud Engineering
        </h1>

        <p style={{ fontSize: "18px", color: "#555", maxWidth: "700px" }}>
          A hands-on course designed to teach how real AWS systems are built —
          from architecture design to production-ready implementation.
        </p>
      </div>


      {/* IMAGE */}
      <div style={{ ...sectionStyle, paddingTop: "0", textAlign: "center" }}>
        <img
          src="/images/realWorldAwsDevelopment.png"
          alt="Real-World AWS Development Course"
          style={{
            width: "100%",
            maxWidth: "750px",
            borderRadius: "14px",
            boxShadow: "0 15px 40px rgba(0,0,0,0.15)"
          }}
        />

        <div style={{ fontSize: "14px", color: "#777", marginTop: "10px" }}>
          Hands-on AWS development with real-world architecture
        </div>
      </div>


      {/* INTRO */}
      <div style={sectionStyle}>
        <p style={{ marginBottom: "20px", lineHeight: "1.7" }}>
          This course is built around a simple idea — learning AWS should be based on
          real systems, not isolated service examples.
        </p>

        <p style={{ lineHeight: "1.7" }}>
          Instead of focusing on individual tools, it walks through a complete project,
          showing how multiple AWS services work together in a production-ready architecture.
        </p>
      </div>


      {/* WHAT YOU LEARN 🔥 */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "25px" }}>
          What You’ll Learn
        </h2>

        <div style={gridStyle}>
          {[
            "Design end-to-end serverless architectures for real-world systems",
            "Build production-ready APIs using API Gateway and AWS Lambda",
            "Model and optimize data access patterns with DynamoDB",
            "Implement event-driven workflows using SNS, SQS, and Step Functions",
            "Design scalable file processing pipelines with S3",
            "Apply infrastructure as code using AWS SAM and CloudFormation",
            "Integrate AI services such as Bedrock, Textract, and Translate",
            "Develop backend logic in Python for cloud-native applications",
            "Understand performance, scalability, and cost trade-offs"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              ✓ {item}
            </div>
          ))}
        </div>
      </div>


      {/* HOW IT'S DIFFERENT 🔥 */}
      <div style={sectionStyle}>
        <h2 style={{ marginBottom: "25px" }}>
          What Makes This Course Different
        </h2>

        <div style={gridStyle}>
          {[
            "Focused on real-world architectures, not isolated services",
            "Built around a complete project, not disconnected examples",
            "Based on real consulting experience and production systems",
            "Combines development, architecture, and best practices",
            "Shows how to integrate multiple AWS services into one system"
          ].map((item, i) => (
            <div
              key={i}
              style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >
              {item}
            </div>
          ))}
        </div>
      </div>


      {/* WHO IT'S FOR */}
      <div style={{ ...sectionStyle, background: "#fafafa" }}>
        <h2 style={{ marginBottom: "25px" }}>
          Who This Course Is For
        </h2>

        <div style={gridStyle}>
          {[
            "Developers starting their AWS journey",
            "Engineers wanting to build real production systems",
            "Teams moving towards serverless architectures",
            "Professionals looking to strengthen practical AWS skills",
            "Anyone tired of 'Hello World' cloud examples"
          ].map((item, i) => (
            <div key={i} style={cardStyle}
              onMouseEnter={(e) => hoverEffect(e, true)}
              onMouseLeave={(e) => hoverEffect(e, false)}
            >{item}</div>
          ))}
        </div>
      </div>


      {/* POSITIONING */}
      <div style={sectionStyle}>
        <p style={{ marginBottom: "20px", lineHeight: "1.7" }}>
          The course reflects the same architectural patterns and development practices
          I apply in real consulting projects.
        </p>

        <p style={{ lineHeight: "1.7" }}>
          It bridges the gap between learning AWS services and building complete, scalable systems.
        </p>
      </div>


      {/* CTA 🔥 */}
      <div style={{
        textAlign: "center",
        margin: "80px 0",
        padding: "50px 20px",
        background: "#fafafa"
      }}>
        <h2 style={{ marginBottom: "15px" }}>
          Start Building Real AWS Systems
        </h2>

        <p style={{ color: "#555", marginBottom: "25px" }}>
          Learn practical cloud development with a complete, hands-on project.
        </p>

        <a
          href="https://www.udemy.com/course/real-world-aws-development/"
          target="_blank"
          rel="noreferrer"
          style={{
            display: "inline-block",
            padding: "14px 32px",
            background: "#007bff",
            color: "#fff",
            borderRadius: "8px",
            textDecoration: "none",
            fontWeight: "500",
            boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
            transition: "all 0.2s ease"
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 12px 30px rgba(0,0,0,0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 8px 20px rgba(0,0,0,0.1)";
          }}
        >
          View Course on Udemy →
        </a>
      </div>

    </div>
  );
};

export default AwsCourseUdemy;